import { useEffect, useState, useRef } from 'react';
import ImageViewer from './ImageViewer';

function ModalImage({ image, onView, containerRef }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="modal-image-item">
      <div className="modal-image-container" ref={containerRef}>
        {imgError ? (
          <div className="modal-screenshot modal-screenshot-fallback">
            <span>Screenshot coming soon</span>
          </div>
        ) : (
          <div onClick={onView}>
            <img
              src={image.src}
              alt={image.caption}
              className="modal-screenshot"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </div>
        )}
      </div>
      <p className="modal-image-caption">{image.caption}</p>
    </div>
  );
}

function ProjectModal({ project, isOpen, onClose }) {
  const [viewingImage, setViewingImage] = useState(null);
  const imageContainerRefs = useRef([]);

  // Reset viewing image when modal closes
  useEffect(() => {
    if (!isOpen) {
      setViewingImage(null);
    }
  }, [isOpen]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Seamless scroll handler
  useEffect(() => {
    if (!isOpen || !project) return;

    const handleWheel = (e, container) => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

      // If scrolling down and at bottom, or scrolling up and at top, allow parent scroll
      if ((e.deltaY > 0 && isAtBottom) || (e.deltaY < 0 && isAtTop)) {
        // Don't stop propagation - let it bubble to parent for seamless scrolling
        return;
      }

      // Otherwise, stop the event from bubbling to prevent parent from scrolling
      e.stopPropagation();
    };

    const containers = imageContainerRefs.current;
    const listeners = containers.map((container) => {
      if (!container) return null;

      const listener = (e) => handleWheel(e, container);
      container.addEventListener('wheel', listener);
      return { container, listener };
    });

    return () => {
      listeners.forEach((item) => {
        if (item) {
          item.container.removeEventListener('wheel', item.listener);
        }
      });
    };
  }, [isOpen, project]);

  if (!isOpen || !project) return null;

  return (
    <div className={`modal-backdrop ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className={`modal-content ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>

        <div className="modal-header">
          <h2 className="modal-title">{project.name}</h2>
          <p className="modal-tagline">{project.tagline}</p>
        </div>

        <div className="modal-description">
          <p>{project.description}</p>
        </div>

        <div className="modal-section">
          <h3>Key Features</h3>
          <ul className="modal-features">
            {project.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>

        <div className="modal-section">
          <h3>Technologies</h3>
          <div className="modal-technologies">
            {project.technologies.map((tech, index) => (
              <span key={index} className="modal-tech-tag">{tech}</span>
            ))}
          </div>
        </div>

        <div className="modal-image-gallery">
          {project.images.map((image, index) => (
            <ModalImage
              key={index}
              image={image}
              onView={() => setViewingImage(image)}
              containerRef={(el) => (imageContainerRefs.current[index] = el)}
            />
          ))}
        </div>

        <div className="modal-links">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="modal-github-link modal-live-link"
            >
              Visit laner.gg
            </a>
          )}
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="modal-github-link"
            >
              View on GitHub
            </a>
          )}
        </div>
      </div>

      <ImageViewer
        imageSrc={viewingImage?.src}
        imageAlt={viewingImage?.caption}
        isOpen={!!viewingImage}
        onClose={() => setViewingImage(null)}
      />
    </div>
  );
}

export default ProjectModal;
